// src/features/monetize/MonetizeApi.ts

import { webApi } from '@/shared/api/WebApi';
import type {
  CancelSubscriptionReq,
  CompReq,
  ConnectState,
  GatingSyncRes,
  MailSenderReq,
  MembershipTemplateReq,
  MonetizeCatalog,
  MonetizeEntitlement,
  MonetizeOffer,
  MonetizePrice,
  MonetizeProfile,
  MonetizeProfileReq,
  MonetizeReport,
  MonetizeStatus,
  OfferReq,
  OfferUpdateReq,
  OnboardingReq,
  OnboardingRes,
  PriceReq,
  ProductMutationRes,
  ProductReq,
  ProductStatus,
  MonetizePurchase,
  MonetizeSubscription,
  MonetizeViewer,
  PreviewGrant,
  RefundReq,
  TicketedLiveTemplateReq,
  ViewerLedger,
} from '@/lib/types/Monetize';

/**
 * The api's `/v1/monetize/v1/...` proxy to commerce-api (Revenue Suite Seq 1).
 * Every call carries the selected account; the api names the acting user to
 * commerce on writes and reconciles the content access rules after a
 * catalog write (`gatingSync` in the answer).
 */
export class MonetizeApi {
  private static readonly base = 'monetize/v1';

  static async getStatus(cId: number): Promise<MonetizeStatus> {
    return webApi.get<MonetizeStatus>(`${this.base}/status`, { cId });
  }

  static async getCatalog(cId: number): Promise<MonetizeCatalog> {
    return webApi.get<MonetizeCatalog>(`${this.base}/catalog`, { cId });
  }

  static async getProfile(cId: number): Promise<MonetizeProfile> {
    return webApi.get<MonetizeProfile>(`${this.base}/profile`, { cId });
  }

  static async saveProfile(cId: number, req: MonetizeProfileReq): Promise<MonetizeProfile> {
    return webApi.put<MonetizeProfile>(`${this.base}/profile`, { cId }, req);
  }

  /** The dashboard: the subscription base now and the range's days (from and to inclusive, YYYY-MM-DD UTC). */
  static async getReport(cId: number, from: string, to: string): Promise<MonetizeReport> {
    return webApi.get<MonetizeReport>(`${this.base}/reports/overview`, { cId, from, to });
  }

  /** The operator's own e-mail sender: set (or clear with an empty domain), then verify the DNS records. */
  static async setMailSender(cId: number, req: MailSenderReq): Promise<MonetizeProfile> {
    return webApi.put<MonetizeProfile>(`${this.base}/profile/mail-sender`, { cId }, req);
  }

  static async verifyMailSender(cId: number): Promise<MonetizeProfile> {
    return webApi.post<MonetizeProfile>(`${this.base}/profile/mail-sender/verify`, { cId });
  }

  static async getConnect(cId: number, refresh = false): Promise<ConnectState> {
    return webApi.get<ConnectState>(`${this.base}/connect`, refresh ? { cId, refresh: 'true' } : { cId });
  }

  static async startOnboarding(cId: number, req: OnboardingReq): Promise<OnboardingRes> {
    return webApi.post<OnboardingRes>(`${this.base}/connect/onboarding`, { cId }, req);
  }

  static async createProduct(cId: number, req: ProductReq): Promise<ProductMutationRes> {
    return webApi.post<ProductMutationRes>(`${this.base}/products`, { cId }, req);
  }

  static async updateProduct(cId: number, productId: string, req: ProductReq): Promise<ProductMutationRes> {
    return webApi.put<ProductMutationRes>(`${this.base}/products/${productId}`, { cId }, req);
  }

  static async setProductStatus(cId: number, productId: string, status: ProductStatus): Promise<ProductMutationRes> {
    return webApi.post<ProductMutationRes>(`${this.base}/products/${productId}/status`, { cId }, { status });
  }

  static async addPrice(cId: number, productId: string, req: PriceReq): Promise<{ price: MonetizePrice }> {
    return webApi.post<{ price: MonetizePrice }>(`${this.base}/products/${productId}/prices`, { cId }, req);
  }

  static async archivePrice(cId: number, priceId: string): Promise<{ price: MonetizePrice }> {
    return webApi.post<{ price: MonetizePrice }>(`${this.base}/prices/${priceId}/archive`, { cId });
  }

  static async createOffer(cId: number, req: OfferReq): Promise<{ offer: MonetizeOffer }> {
    return webApi.post<{ offer: MonetizeOffer }>(`${this.base}/offers`, { cId }, req);
  }

  static async updateOffer(cId: number, offerId: string, req: OfferUpdateReq): Promise<{ offer: MonetizeOffer }> {
    return webApi.put<{ offer: MonetizeOffer }>(`${this.base}/offers/${offerId}`, { cId }, req);
  }

  static async archiveOffer(cId: number, offerId: string): Promise<{ offer: MonetizeOffer }> {
    return webApi.post<{ offer: MonetizeOffer }>(`${this.base}/offers/${offerId}/archive`, { cId });
  }

  static async applyMembership(cId: number, req: MembershipTemplateReq): Promise<MonetizeCatalog> {
    return webApi.post<MonetizeCatalog>(`${this.base}/templates/membership`, { cId }, req);
  }

  static async applyTicketedLive(cId: number, req: TicketedLiveTemplateReq): Promise<MonetizeCatalog> {
    return webApi.post<MonetizeCatalog>(`${this.base}/templates/ticketed_live`, { cId }, req);
  }

  // ---- the viewer's ledger (Seq 1 step 4) ----

  /** The operator's viewer for an e-mail address (404 `viewer_unknown` when none signed in with it). */
  static async findViewer(cId: number, email: string): Promise<MonetizeViewer> {
    return webApi.get<MonetizeViewer>(`${this.base}/viewers`, { cId, email });
  }

  static async getViewerLedger(cId: number, viewerId: string): Promise<ViewerLedger> {
    return webApi.get<ViewerLedger>(`${this.base}/viewers/${viewerId}/ledger`, { cId });
  }

  static async comp(cId: number, viewerId: string, req: CompReq): Promise<MonetizeEntitlement> {
    return webApi.post<MonetizeEntitlement>(`${this.base}/viewers/${viewerId}/entitlements`, { cId }, req);
  }

  static async revokeEntitlement(cId: number, entitlementId: string, reason: string): Promise<MonetizeEntitlement> {
    return webApi.post<MonetizeEntitlement>(`${this.base}/entitlements/${entitlementId}/revoke`, { cId }, { reason });
  }

  static async extendEntitlement(cId: number, entitlementId: string, endsAt: string, reason: string): Promise<MonetizeEntitlement> {
    return webApi.post<MonetizeEntitlement>(`${this.base}/entitlements/${entitlementId}/extend`, { cId }, { endsAt, reason });
  }

  static async refundPurchase(cId: number, purchaseId: string, req: RefundReq): Promise<{ purchase: MonetizePurchase }> {
    return webApi.post<{ purchase: MonetizePurchase }>(`${this.base}/purchases/${purchaseId}/refund`, { cId }, req);
  }

  static async cancelSubscription(cId: number, subscriptionId: string, req: CancelSubscriptionReq): Promise<{ subscription: MonetizeSubscription }> {
    return webApi.post<{ subscription: MonetizeSubscription }>(`${this.base}/subscriptions/${subscriptionId}/cancel`, { cId }, req);
  }

  /** A short-lived pass for the panel's own preview of gated content (Seq 1 step 8); scope = media:<id> | list:<id>. */
  static async previewGrant(cId: number, scope: string): Promise<PreviewGrant> {
    return webApi.post<PreviewGrant>(`${this.base}/preview/grant`, { cId }, { scope });
  }

  /** Re-apply the content access rules the catalog implies (the Re-apply button; the page runs it on load). */
  static async syncGating(cId: number): Promise<GatingSyncRes> {
    return webApi.post<GatingSyncRes>(`${this.base}/gating/sync`, { cId });
  }
}
